import { yupResolver } from '@hookform/resolvers/yup'
import { getEFinancingProgramList, getEOriginSystemIdList, getETemplateMessageCategoryList } from '@if/core/types'
import { useTranslatedOptionEnum } from '@if/mui'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  useTheme
} from '@mui/material'
import ServerErrorsAlert from '@src/components/ServerErrorsAlert'
import { useGetTemplateMessagesAndPlaceholderskeys } from '@src/data/api/chat-message/chat-message-api'
import { TemplateMessageRequestDto, TemplateMessageRequestForm, TemplateMessageResponseDto, buildTemplateMessageSchema } from '@src/data/types/TemplateMessageResponseDto'
import { memo, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import PlaceholderMention from './PlaceholderMention'

interface Props {
  open: boolean
  onConfirm: (data: TemplateMessageRequestDto) => Promise<boolean>
  onCancel: () => void
  message?: TemplateMessageResponseDto | null
  isLoading?: boolean
  isViewMode?: boolean
  apiError?: Error | null
  readonly?: {
    messageFr?: boolean
    messageEn?: boolean
    category?: boolean
  }
}

const TemplateMessageModal = ({
  open,
  message,
  isViewMode = false,
  onCancel,
  onConfirm,
  isLoading = false,
  apiError,
  readonly = {},
}: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [templateMessagesWithPlaceholders] = useGetTemplateMessagesAndPlaceholderskeys()
  const allPlaceholders = templateMessagesWithPlaceholders.placeholders

  const schema = useMemo(() => buildTemplateMessageSchema(allPlaceholders), [allPlaceholders])

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
    watch,
  } = useForm<TemplateMessageRequestForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      Code: message?.code || null,
      MessageFr: message?.messageFr || '',
      MessageEn: message?.messageEn || '',
      Category: message?.category || '',
      FinancingProgramIds: message?.financingProgramIds || [],
      OriginSystemIds: message?.originSystemIds || [],
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        Code: message?.code || null,
        MessageFr: message?.messageFr || '',
        MessageEn: message?.messageEn || '',
        Category: message?.category || '',
        FinancingProgramIds: message?.financingProgramIds || [],
        OriginSystemIds: message?.originSystemIds || [],
      })
    }
  }, [message, open, reset])


  const categoryList = getETemplateMessageCategoryList()
  const categoryOptions = useTranslatedOptionEnum('ETemplateMessageCategory', categoryList, 'enum')
  const financingProgramList = getEFinancingProgramList()
  const financingProgramOptions = useTranslatedOptionEnum('EFinancingProgram', financingProgramList, 'enum')
  const originSystemList = getEOriginSystemIdList()
  const originSystemOptions = useTranslatedOptionEnum('EOriginSystemId', originSystemList, 'enum')

  const onSubmit = async (data: TemplateMessageRequestForm) => {
    const result = await onConfirm(data as TemplateMessageRequestDto)
    if (result !== false) {
      onCancel()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      transitionDuration={0}
      disablePortal
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {isViewMode
          ? t('instantMessage.viewMessage')
          : message
            ? t('instantMessage.editMessage')
            : t('instantMessage.createMessage')}
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {apiError && <ServerErrorsAlert serverErrors={apiError} />}
          <Stack spacing={5}>
            {message && (
              <TextField
                fullWidth
                label={t('instantMessage.messageCode')}
                value={message.code || ''}
                disabled
                size="small"
              />
            )}

            <Stack direction="row" spacing={2}>
              {isViewMode ? (
                <>
                  <TextField
                    fullWidth
                    label={t('instantMessage.category')}
                    value={t(`ETemplateMessageCategory.${watch('Category')}`, { ns: 'enum', defaultValue: watch('Category') })}
                    disabled
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label={t('instantMessage.financingProgram')}
                    value={watch('FinancingProgramIds').length > 0
                      ? watch('FinancingProgramIds').map(id => t(`EFinancingProgram.${id}`, { ns: 'enum', defaultValue: id })).join(', ')
                      : '-'}
                    disabled
                    size="small"
                  />
                </>
            ) : (
                <>
                  <Controller
                    name="Category"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.Category}>
                        <InputLabel>{t('instantMessage.category')}</InputLabel>
                        <Select
                          {...field}
                          label={t('instantMessage.category')}
                          disabled={!!message || isViewMode || readonly.category}
                        >
                      {categoryOptions.map((option) => {
                        const props = option?.props as { value: string; children: string } | undefined
                        return props ? (
                          <MenuItem key={props.value} value={props.value}>
                            {props.children}
                          </MenuItem>
                        ) : null
                      })}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="FinancingProgramIds"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.FinancingProgramIds}>
                        <InputLabel>{t('instantMessage.financingProgram')}</InputLabel>
                        <Select
                          {...field}
                          multiple
                          label={t('instantMessage.financingProgram')}
                          disabled={!!message || isViewMode}
                          value={field.value || []}
                          renderValue={(selected) =>
                            selected
                              .map(id => t(`EFinancingProgram.${id}`, { ns: 'enum', defaultValue: id }))
                              .join(', ')
                          }
                        >
                          {financingProgramOptions.map((option) => {
                            const props = option?.props as { value: string; children: string } | undefined
                            return props ? (
                              <MenuItem key={props.value} value={props.value}>
                                <Checkbox checked={field.value?.includes(props.value)} />
                                <ListItemText primary={props.children} />
                              </MenuItem>
                            ) : null
                          })}
                        </Select>
                      </FormControl>
                    )}
                  />
                </>
            )}
            </Stack>

            <Box>
              <Controller
                name="MessageFr"
                control={control}
                render={({ field }) => {
                  const errorKey = typeof errors.MessageFr?.message === 'string' ? errors.MessageFr.message : undefined
                  const errorMessage: string = errorKey ? (t as (key: string) => string)(errorKey) : t('instantMessage.insertPlaceholder')
                  return (
                    <PlaceholderMention
                      {...field}
                      label={t('instantMessage.messageFr')}
                      multiline
                      rows={4}
                      size="small"
                      disabled={isViewMode || readonly.messageFr || watch('FinancingProgramIds').length === 0}
                      helperText={
                        !isViewMode && !readonly.messageFr && watch('FinancingProgramIds').length === 0
                          ? t('instantMessage.fillProgramFirst')
                          : errorMessage
                      }
                      error={!!errors.MessageFr}
                      fullWidth
                      financingProgramIds={watch('FinancingProgramIds')}
                    />
                  )
                }}
              />
            </Box>

            <Box>
              <Controller
                name="MessageEn"
                control={control}
                render={({ field }) => {
                  const errorKey = typeof errors.MessageEn?.message === 'string' ? errors.MessageEn.message : undefined
                  const errorMessage: string = errorKey ? (t as (key: string) => string)(errorKey) : t('instantMessage.insertPlaceholder')
                  return (
                    <PlaceholderMention
                      {...field}
                      label={t('instantMessage.messageEn')}
                      multiline
                      rows={4}
                      size="small"
                      disabled={isViewMode || readonly.messageEn || watch('FinancingProgramIds').length === 0}
                      helperText={
                        !isViewMode && !readonly.messageEn && watch('FinancingProgramIds').length === 0
                          ? t('instantMessage.fillProgramFirst')
                          : errorMessage
                      }
                      error={!!errors.MessageEn}
                      fullWidth
                      financingProgramIds={watch('FinancingProgramIds')}
                    />
                  )
                }}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              {isViewMode ? (
                <TextField
                  fullWidth
                  label={t('instantMessage.originSystem')}
                  value={watch('OriginSystemIds')?.length ? watch('OriginSystemIds').map((id) => t(`EOriginSystemId.${id}`, { ns: 'enum', defaultValue: id })).join(', ') : '-'}
                  disabled
                  size="small"
                />
              ) : (
                <Controller
                  name="OriginSystemIds"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.OriginSystemIds}>
                      <InputLabel>{t('instantMessage.originSystem')}</InputLabel>
                      <Select
                        {...field}
                        multiple
                        label={t('instantMessage.originSystem')}
                        disabled={!!message || isViewMode}
                        value={field.value || []}
                      >
                        {originSystemOptions.map((option) => {
                          const props = option?.props as { value: string; children: string } | undefined
                          return props ? (
                            <MenuItem key={props.value} value={props.value}>
                              {props.children}
                            </MenuItem>
                          ) : null
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              )}
            </Stack>

          <Divider />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
              {!isViewMode && (
              <>
                  <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                    type="submit"
                >
                  {t('common.save')}
                </Button>
              </>
            )}
          </Stack>
          </Stack>
        </DialogContent>
      </form>
    </Dialog>
  )
}

export default memo(TemplateMessageModal)
